import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest } from '@/types';
import { API_ATTEND_TRACK_URL } from '@/utils';
import {
  InboxApiResponse,
  InboxFilter,
  ServeCustomer,
  FinalizeService,
} from '../models';

const inboxService = createApi({
  reducerPath: 'inboxService',
  baseQuery: createBaseQueryWithReauth(API_ATTEND_TRACK_URL),
  tagTypes: ['Inboxes'] as const,
  endpoints: (builder) => ({
    getInbox: builder.query<
      InboxApiResponse,
      { filters: InboxFilter; paginated: PaginatedRequest }
    >({
      query: ({ filters, paginated }) => ({
        url: '/inboxes',
        params: {
          shippingId: filters.shippingId,
          phoneNumber: filters.phoneNumber,
          documentNumber: filters.documentNumber,
          sectorId: filters.sector.id,
          status: filters.statuses.toString(),
          offset: (paginated.page - 1) * paginated.pageSize,
          limit: paginated.pageSize,
        },
      }),
      providesTags: ['Inboxes'],
    }),
    serveCustomer: builder.mutation<unknown, ServeCustomer>({
      query: ({ inboxId, attentionUser }) => ({
        url: '/inbox/serve-customer',
        method: 'POST',
        body: { inboxId, attentionUser },
      }),
      invalidatesTags: ['Inboxes'],
    }),
    finalizeService: builder.mutation<unknown, FinalizeService>({
      query: ({ inboxId, documentNumber, success }) => ({
        url: '/inbox/finalize-service',
        method: 'POST',
        body: { inboxId, documentNumber, success },
      }),
      invalidatesTags: ['Inboxes'],
    }),
    markAsPending: builder.mutation<unknown, string>({
      query: (inboxId) => ({
        url: `/inbox/${inboxId}/status`,
        method: 'PATCH',
        body: { status: 'PENDING' },
      }),
    }),
    updateInboxTag: builder.mutation<unknown, { inboxId: string; tag: string }>(
      {
        query: ({ inboxId, tag }) => ({
          url: `/inbox/${inboxId}/tag`,
          method: 'PATCH',
          body: { tag },
        }),
        invalidatesTags: ['Inboxes'],
      }
    ),
    toggleInboxIsTaken: builder.mutation<
      unknown,
      { inboxId: string; takenById: string; takenByName: string }
    >({
      query: ({ inboxId, takenById, takenByName }) => ({
        url: `/inbox/${inboxId}/toggle-is-taken`,
        method: 'PATCH',
        body: { takenById, takenByName },
      }),
      invalidatesTags: ['Inboxes'],
    }),
  }),
});

export default inboxService;
export const {
  useGetInboxQuery,
  useServeCustomerMutation,
  useFinalizeServiceMutation,
  useMarkAsPendingMutation,
  useUpdateInboxTagMutation,
  useToggleInboxIsTakenMutation,
} = inboxService;
